import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { authClient } from "./authClient";

const AuthContext = createContext(null);
const SESSION_KEY = "morfeusz:auth:session";
const LEGACY_USERS_KEY = "morfeusz:auth:users";
const SESSION_EXPIRED_NOTICE_KEY = "morfeusz:auth:sessionExpiredNotice";
const SESSION_EXPIRED_EVENT = "morfeusz:auth:sessionExpired";
const VAULT_SESSION_EVENT = "morfeusz:vault:session";
const VAULT_LOCKED_EVENT = "morfeusz:vault:locked";
const VAULT_RECONCILE_DELAY_MS = 900;
const VAULT_LOGIN_GRACE_MS = 12000;
const AUTH_RECHECK_THROTTLE_MS = 15000;
const AUTH_BACKGROUND_CHECK_MS = 60000;
const AUTH_RECHECK_EVENTS = ["pointerdown", "keydown", "touchstart"];

const readSession = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
};

const writeSession = (session) => {
  try {
    if (session) {
      window.localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    } else {
      window.localStorage.removeItem(SESSION_KEY);
    }
  } catch (_) {
    /* ignore */
  }
};

const markSessionExpiredNotice = () => {
  if (typeof window === "undefined") return;
  const at = Date.now();
  const payload = JSON.stringify({ at });
  try {
    window.sessionStorage.setItem(SESSION_EXPIRED_NOTICE_KEY, payload);
  } catch (_) {
    try {
      window.localStorage.setItem(SESSION_EXPIRED_NOTICE_KEY, payload);
    } catch (__) {
      /* ignore */
    }
  }
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT, { detail: { at } }));
};

const clearLegacyAuthStorage = () => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(LEGACY_USERS_KEY);
  } catch (_) {
    /* ignore */
  }
};

const toSession = (user) => ({
  id: user.id,
  email: user.email,
  login: user.login,
  displayName: user.displayName,
  createdAt: user.createdAt,
  emailConfirmed: user.emailConfirmed,
  source: "backend",
});

const authUnavailableError = () => {
  const error = new Error("Logowanie backendowe jest chwilowo niedostępne.");
  error.code = "auth_unavailable";
  return error;
};

const unlockVaultWithAccountPassword = async (password, options = {}) => {
  if (typeof window === "undefined") return null;
  const session = window.MDVaultSession;
  if (!session || typeof session.ensureUnlockedWithPassphrase !== "function") return null;
  return session.ensureUnlockedWithPassphrase(password, options).catch((error) => ({
    ok: false,
    reason: error?.message || "vault_unlock_failed",
  }));
};

const isVaultSessionReady = () => {
  if (typeof window === "undefined") return false;
  return Boolean(
    window.MDVaultSession?.getState
    || window.MDVaultSession?.isUnlocked
    || window.morfeuszE2E?.isUnlocked
  );
};

const isVaultUnlocked = () => {
  if (typeof window === "undefined") return false;
  return Boolean(window.MDVaultSession?.isUnlocked?.() || window.morfeuszE2E?.isUnlocked?.());
};

const clearFrontendSession = () => {
  if (typeof window !== "undefined") {
    window.MDVaultSession?.lock?.();
  }
  authClient.clearToken();
  writeSession(null);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(readSession);
  const [authReady, setAuthReady] = useState(false);
  const [authMode, setAuthMode] = useState("checking");
  const userRef = useRef(user);
  const authReadyRef = useRef(authReady);
  const internalLogoutRef = useRef(false);
  const expiringRef = useRef(false);
  const authRecheckInFlightRef = useRef(false);
  const lastAuthRecheckAtRef = useRef(0);
  const vaultWasUnlockedRef = useRef(false);
  const vaultUnlockingAfterLoginRef = useRef(false);
  const vaultLoginGraceUntilRef = useRef(0);

  const isVaultLoginGraceActive = useCallback(() => {
    return vaultUnlockingAfterLoginRef.current || Date.now() < vaultLoginGraceUntilRef.current;
  }, []);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    authReadyRef.current = authReady;
  }, [authReady]);

  const expireWholeSession = useCallback(async () => {
    if (internalLogoutRef.current || expiringRef.current || !userRef.current) return;
    expiringRef.current = true;
    try {
      if (authClient.isConfigured()) {
        await authClient.revoke?.().catch(() => null);
      }
    } finally {
      markSessionExpiredNotice();
      clearFrontendSession();
      vaultWasUnlockedRef.current = false;
      vaultUnlockingAfterLoginRef.current = false;
      vaultLoginGraceUntilRef.current = 0;
      setUser(null);
      setAuthMode(authClient.isConfigured() ? "backend" : "backend-missing");
      if (typeof window !== "undefined" && (window.location.hash || "") !== "#top") window.location.hash = "#top";
      expiringRef.current = false;
    }
  }, []);

  const reconcileBackendAuthSession = useCallback(async ({ force = false } = {}) => {
    if (!authReadyRef.current || !userRef.current || internalLogoutRef.current || expiringRef.current) return;
    if (!authClient.isConfigured() || authRecheckInFlightRef.current) return;

    const now = Date.now();
    if (!force && now - lastAuthRecheckAtRef.current < AUTH_RECHECK_THROTTLE_MS) return;
    lastAuthRecheckAtRef.current = now;
    authRecheckInFlightRef.current = true;

    try {
      const response = await authClient.me();
      if (!response?.user && userRef.current) {
        await expireWholeSession();
      }
    } catch (_) {
      if (userRef.current) await expireWholeSession();
    } finally {
      authRecheckInFlightRef.current = false;
    }
  }, [expireWholeSession]);

  const expireIfLoggedInVaultLocked = useCallback(() => {
    if (!authReadyRef.current || !userRef.current || internalLogoutRef.current || expiringRef.current) return;
    if (isVaultLoginGraceActive()) return;
    if (!isVaultSessionReady()) return;
    if (isVaultUnlocked()) {
      vaultWasUnlockedRef.current = true;
      return;
    }
    expireWholeSession();
  }, [expireWholeSession, isVaultLoginGraceActive]);

  useEffect(() => {
    let mounted = true;

    const boot = async () => {
      clearLegacyAuthStorage();

      if (!authClient.isConfigured()) {
        writeSession(null);
        authClient.clearToken();
        vaultWasUnlockedRef.current = false;
        if (!mounted) return;
        setUser(null);
        setAuthMode("backend-missing");
        setAuthReady(true);
        return;
      }

      try {
        const response = await authClient.me();
        if (!mounted) return;
        const session = response.user ? toSession(response.user) : null;
        writeSession(session);
        setUser(session);
        setAuthMode("backend");
        if (session && isVaultUnlocked()) {
          vaultWasUnlockedRef.current = true;
        }
      } catch (_) {
        writeSession(null);
        authClient.clearToken();
        vaultWasUnlockedRef.current = false;
        if (!mounted) return;
        setUser(null);
        setAuthMode("backend-error");
      } finally {
        if (mounted) setAuthReady(true);
      }
    };

    boot();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const sync = () => setUser(readSession());
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    if (userRef.current && isVaultUnlocked()) {
      vaultWasUnlockedRef.current = true;
    }

    const onVaultLocked = () => {
      if (isVaultLoginGraceActive()) return;
      if (userRef.current) expireWholeSession();
    };

    const onVaultSession = (event) => {
      const detail = event?.detail || {};
      if (detail.unlocked === true || isVaultUnlocked()) {
        vaultWasUnlockedRef.current = true;
        vaultUnlockingAfterLoginRef.current = false;
        vaultLoginGraceUntilRef.current = 0;
        return;
      }
      if (isVaultLoginGraceActive()) return;
      if (detail.unlocked === false && userRef.current && !isVaultUnlocked()) {
        expireWholeSession();
      }
    };

    window.addEventListener(VAULT_LOCKED_EVENT, onVaultLocked);
    window.addEventListener(VAULT_SESSION_EVENT, onVaultSession);
    return () => {
      window.removeEventListener(VAULT_LOCKED_EVENT, onVaultLocked);
      window.removeEventListener(VAULT_SESSION_EVENT, onVaultSession);
    };
  }, [expireWholeSession, isVaultLoginGraceActive]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    let timeoutId = 0;

    const scheduleReconcile = () => {
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => {
        reconcileBackendAuthSession({ force: true });
        expireIfLoggedInVaultLocked();
      }, VAULT_RECONCILE_DELAY_MS);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") scheduleReconcile();
    };

    scheduleReconcile();
    window.addEventListener("focus", scheduleReconcile);
    window.addEventListener("pageshow", scheduleReconcile);
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener(VAULT_SESSION_EVENT, scheduleReconcile);

    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener("focus", scheduleReconcile);
      window.removeEventListener("pageshow", scheduleReconcile);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener(VAULT_SESSION_EVENT, scheduleReconcile);
    };
  }, [authReady, user, expireIfLoggedInVaultLocked, reconcileBackendAuthSession]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    let intervalId = 0;

    const onActivity = () => {
      reconcileBackendAuthSession();
    };

    AUTH_RECHECK_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, onActivity, { passive: true });
    });

    intervalId = window.setInterval(() => reconcileBackendAuthSession(), AUTH_BACKGROUND_CHECK_MS);

    return () => {
      AUTH_RECHECK_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, onActivity);
      });
      window.clearInterval(intervalId);
    };
  }, [reconcileBackendAuthSession]);

  const login = useCallback(async (email, password) => {
    if (!authClient.isConfigured()) throw authUnavailableError();

    const identifier = String(email || "").trim();
    vaultUnlockingAfterLoginRef.current = true;
    vaultLoginGraceUntilRef.current = Date.now() + VAULT_LOGIN_GRACE_MS;
    lastAuthRecheckAtRef.current = Date.now();

    try {
      const response = await authClient.login({ email: identifier, password });
      if (response.token) authClient.setToken(response.token);
      const session = toSession(response.user);
      writeSession(session);
      clearLegacyAuthStorage();
      setUser(session);
      setAuthMode("backend");

      const vault = await unlockVaultWithAccountPassword(password, { createIfMissing: true });
      if (vault?.ok || vault?.state?.unlocked || isVaultUnlocked()) {
        vaultWasUnlockedRef.current = true;
        vaultUnlockingAfterLoginRef.current = false;
        vaultLoginGraceUntilRef.current = 0;
        lastAuthRecheckAtRef.current = Date.now();
      } else {
        vaultUnlockingAfterLoginRef.current = false;
      }
      return { ...session, vault };
    } catch (error) {
      vaultUnlockingAfterLoginRef.current = false;
      vaultLoginGraceUntilRef.current = 0;
      throw error;
    }
  }, []);

  const register = useCallback(async (email, password, displayName) => {
    if (!authClient.isConfigured()) throw authUnavailableError();

    const cleanEmail = String(email || "").trim().toLowerCase();
    const response = await authClient.register({ email: cleanEmail, password, displayName });
    clearLegacyAuthStorage();
    setAuthMode("backend");
    return response;
  }, []);

  const logout = useCallback(async () => {
    internalLogoutRef.current = true;
    try {
      if (typeof window !== "undefined") {
        window.MDVaultSession?.lock?.();
      }
      if (authClient.isConfigured()) {
        await authClient.logout().catch(() => null);
      }
      authClient.clearToken();
      writeSession(null);
      vaultWasUnlockedRef.current = false;
      vaultUnlockingAfterLoginRef.current = false;
      vaultLoginGraceUntilRef.current = 0;
      setUser(null);
    } finally {
      window.setTimeout(() => {
        internalLogoutRef.current = false;
      }, 0);
    }
  }, []);

  const updateProfile = useCallback(
    (patch) => {
      if (!user) return;
      const next = { ...user, ...patch };
      writeSession(next);
      setUser(next);
    },
    [user],
  );

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: !!user,
      authReady,
      authMode,
      login,
      register,
      logout,
      updateProfile,
    }),
    [user, authReady, authMode, login, register, logout, updateProfile],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    return {
      user: null,
      isAuthenticated: false,
      authReady: true,
      authMode: "missing",
      login: async () => {},
      register: async () => {},
      logout: () => {},
      updateProfile: () => {},
    };
  }
  return ctx;
};
